/**
 * @fileoverview TogglePillGroup — the ONE interactive segmented-pill chrome (atom)
 * @package @puffin-core/page-shell
 *
 * Not to be confused with `SectionPill`, which is a decorative capsule LABEL on
 * `.widget-section-pill`. This is the INTERACTIVE `.toggle-pill` control.
 *
 * ## Why this exists (PLN-SP-UAT-2026-08 Round 4, ITEM 10)
 *
 * W15's placement ruling said `SolveForColumnPill` would be "refactored to
 * compose the same primitive … so Step 2 and Step 3 cannot drift". That promise
 * was never kept: both components consumed the same `forms.css` classes but each
 * hand-wrote its own `role="radiogroup"` wrapper and its own button loop. Shared
 * CSS with two independent JSX implementations is exactly the drift risk the
 * ruling named — a change to the a11y contract, the data attributes, or the
 * active-state class had to be made twice, and one of them was already behind
 * (`PlanCalcPill` stamped no `data-pill-*` attributes and gave its inert
 * position no visual treatment at all).
 *
 * ## Why the primitive is this shape, and not `PlanCalcPill` itself
 *
 * A literal "`SolveForColumnPill` renders `PlanCalcPill`" cannot be built,
 * because the two are different *semantics* wearing the same chrome:
 *
 *   - `SolveForColumnPill` is a genuine binary MUTEX — two metrics, both
 *     positions live, clicking either inactive one flips the pair.
 *   - `PlanCalcPill` is 1-of-N — only "Calc" is actionable, and "Plan" on the
 *     calc child is INERT, because something has to absorb the remainder.
 *
 * Generalising one into the other would break whichever lost. So the chrome —
 * the wrapper, the button loop, the class composition, the ARIA and the data
 * attributes — moves HERE, and each component keeps its own semantics by
 * describing them as options. One implementation, two meanings.
 *
 * ## The inert position
 *
 * An inert option is `aria-disabled`, never natively `disabled` — the same rule
 * PS-003 applies to `<WizardActionBar>`: a truly disabled button leaves the tab
 * order and announces nothing, while `aria-disabled` stays reachable and can
 * explain its refusal through `aria-describedby`. The refusal ALSO lives in the
 * handler (`onClick` is `undefined`), because `aria-disabled` is advisory to the
 * browser and a real click still fires.
 *
 * Its muted look hangs off `data-pill-inert`, mirroring `.btn[data-blocked]` in
 * `wizard.css`, and deliberately keeps `pointer-events` ON so the cursor can
 * reach the button to read its reason and show `not-allowed`.
 * `.toggle-pill-btn-disabled` is NOT used here: it sets `pointer-events: none`,
 * which would suppress the very tooltip that explains the refusal.
 *
 * ## Placement — promoted here on the owner's ruling (R-52, 2026-08-14)
 *
 * It was built in planner-web `shared/`, on this repo's usual "packages/ is for
 * 2+ apps" rule, with a promotion trigger in its docblock. The owner overrode
 * that on sight: *"I want to make sure you built it in a factory-first,
 * shared-css way so that similar use cases in the future can leverage it.
 * basically, we do not support bespoke or inline elements."*
 *
 * The census behind the ruling — the `.toggle-pill` chrome was ALREADY
 * hand-written four times:
 *
 *   1. `planner-web` `SolveForColumnPill`   — composes this (`radio`)
 *   2. `planner-web` `PlanCalcPill`         — composes this (`radio`)
 *   3. `@puffin-core/login-shell` `AuthMethodSelector` — composes this (`tab`)
 *   4. `@puffin-core/re-auth` `AuthMethodSelector`     — composes this (`tab`)
 *
 * So "wait for a second app" was the wrong test: three other apps' worth of
 * consumers already existed, and waiting is how (3) and (4) came to be
 * near-duplicates of each other.
 *
 * ## Two variants, because there are genuinely two a11y contracts
 *
 * (3) and (4) are `role="tablist"` / `role="tab"` / `aria-selected` with
 * arrow-key roving; (1) and (2) are `role="radiogroup"` / `role="radio"` /
 * `aria-checked`. Same CSS, different semantics — so `variant` selects the
 * contract rather than flattening one onto the other, which would have broken
 * whichever lost. The roving keyboard belongs to the `tab` variant because it is
 * part of the tablist ROLE; the `radio` variant deliberately keeps native
 * per-button tabbing, since its consumers live inside grids with their own
 * arrow-key navigation.
 *
 * Chrome classes (`.toggle-pill`, `.toggle-pill-btn`, `.toggle-pill-btn-active`,
 * `.toggle-pill-sm`) live in `@puffin-core/shared-styles`' `forms.css` per
 * CSS-001. This component composes them; it defines none of its own, and it
 * carries no inline styles.
 *
 * @module @puffin-core/page-shell/components/TogglePillGroup
 */

import { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * One option in the pill.
 *
 * `value` is the stable internal key — it is never rendered, it is the React
 * key, the `data-pill-state` value, and the default test-id suffix. `label` is
 * the already-translated text; this component holds no translator, so a caller
 * that needs i18n resolves it at its own render boundary.
 */
export const TOGGLE_PILL_OPTION_SHAPE = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  isActive: PropTypes.bool.isRequired,
  /** Per-button accessible name. Falls back to the visible label. */
  ariaLabel: PropTypes.string,
  /**
   * Reachable and announced, but refuses selection. See the docblock.
   *
   * ⚠️ NOT the same as `disabled`. `inert` keeps `pointer-events` so the cursor
   * can reach the button and read WHY it refuses; `disabled` is a genuinely
   * unavailable choice (a passkey on a device with no authenticator) and takes
   * `.toggle-pill-btn-disabled`, which sets `pointer-events: none`. Collapsing
   * the two would either mute an explanation or imply an option is offerable
   * when it is not.
   */
  inert: PropTypes.bool,
  /** Genuinely unavailable — see the note on `inert`. */
  disabled: PropTypes.bool,
  /** Trailing status content inside the button (e.g. a "Checking…" badge). */
  badge: PropTypes.node,
  /** `tab` variant only: the id of the panel this tab controls. */
  controls: PropTypes.string,
  /** Extra `data-*` attributes for this button. */
  dataAttrs: PropTypes.object,
  /** Called on click when the option is selectable. */
  onSelect: PropTypes.func,
});

/**
 * @param {Object} props
 * @param {Array} props.options - Ordered options; see `TOGGLE_PILL_OPTION_SHAPE`.
 * @param {string} props.ariaLabel - Accessible name for the radiogroup, already translated.
 * @param {'sm'|'md'} [props.size] - `'sm'` adds `.toggle-pill-sm` (dense headers).
 * @param {string} props.testId - `data-testid` for the group; also the default per-option stem.
 * @param {Function} [props.optionTestId] - `(option) => string`, when per-option ids are not `${testId}-${value}`.
 * @param {string} [props.inertHint] - Already-translated explanation of why an inert option refuses.
 * @param {string} [props.inertHintId] - Element id tying `inertHint` to the inert option. Required when `inertHint` is given.
 * @param {'radio'|'tab'} [props.variant='radio'] - The a11y contract: a radiogroup, or a tablist whose tabs
 *   control panels (see "Two variants" above).
 * @param {string} [props.className] - Extra classes on the group, beside `toggle-pill`.
 * @returns {React.JSX.Element}
 */
export function TogglePillGroup({
  options,
  ariaLabel,
  size = 'md',
  testId,
  optionTestId,
  inertHint,
  inertHintId,
  variant = 'radio',
  className,
}) {
  const isTabs = variant === 'tab';
  const listRef = useRef(null);

  // `tab` variant only. The ARIA tablist pattern makes the GROUP one tab stop
  // and moves between options with the arrow keys, so the roving lives here —
  // it is part of the role, not a nicety a consumer should re-implement. The
  // `radio` variant deliberately keeps native per-button tabbing: its consumers
  // are 2-3 position mode switches inside dense grids, where stealing the arrow
  // keys would fight the grid's own roving navigation.
  const handleKeyDown = useCallback(
    (event, currentIndex) => {
      const count = options.length;
      let nextIndex;
      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          nextIndex = (currentIndex + 1) % count;
          break;
        case 'ArrowLeft':
          event.preventDefault();
          nextIndex = (currentIndex - 1 + count) % count;
          break;
        case 'Home':
          event.preventDefault();
          nextIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          nextIndex = count - 1;
          break;
        case 'Enter':
        case ' ': {
          event.preventDefault();
          const option = options[currentIndex];
          if (!option.isActive && !option.inert && !option.disabled) option.onSelect?.();
          return;
        }
        default:
          return;
      }
      const tabs = listRef.current?.querySelectorAll('[role="tab"]');
      if (tabs && tabs[nextIndex]) tabs[nextIndex].focus();
    },
    [options],
  );

  // The hint is rendered once for the group rather than per option: only one
  // position can be inert at a time in every consumer, and a duplicated id
  // would break the `aria-describedby` reference it exists to serve.
  const hasInert = options.some((option) => option.inert);
  const describedBy = hasInert && inertHint ? inertHintId : undefined;

  // A tablist is a `<div role="tablist">` by convention and this stays a `<span>`
  // for the radio variant, because a radiogroup routinely sits INSIDE a table
  // cell or a header row where a block element would break the line box.
  const Wrapper = isTabs ? 'div' : 'span';

  return (
    <Wrapper
      ref={isTabs ? listRef : undefined}
      className={[`toggle-pill${size === 'sm' ? ' toggle-pill-sm' : ''}`, className]
        .filter(Boolean)
        .join(' ')}
      role={isTabs ? 'tablist' : 'radiogroup'}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {options.map((option, index) => {
        const classNames = ['toggle-pill-btn'];
        if (option.isActive) classNames.push('toggle-pill-btn-active');
        // `disabled` is the UNAVAILABLE state and takes the class that turns
        // pointer events off; `inert` is the refusal-with-a-reason state and
        // deliberately does not. See TOGGLE_PILL_OPTION_SHAPE.
        if (option.disabled) classNames.push('toggle-pill-btn-disabled');
        const blocked = option.inert || option.disabled;
        return (
          <button
            key={option.value}
            type="button"
            role={isTabs ? 'tab' : 'radio'}
            {...(isTabs
              ? {
                  'aria-selected': option.isActive ? 'true' : 'false',
                  // Explicitly 'true'/'false' rather than omitted: a tab that
                  // is merely unavailable must still ANNOUNCE that it is,
                  // where a radiogroup's inert position is the exception.
                  'aria-disabled': option.disabled ? 'true' : 'false',
                  // The tablist owns ONE tab stop; arrows move within it.
                  tabIndex: option.isActive ? 0 : -1,
                  'aria-controls': option.controls,
                  onKeyDown: (event) => handleKeyDown(event, index),
                }
              : {
                  'aria-checked': option.isActive,
                  'aria-disabled': option.inert || undefined,
                })}
            aria-label={option.ariaLabel}
            aria-describedby={option.inert ? describedBy : undefined}
            title={option.inert ? inertHint : undefined}
            className={classNames.join(' ')}
            // Defence in depth: the refusal lives in the handler, not only in
            // the ARIA. An already-active option is a no-op for the same reason
            // — re-selecting it cannot change anything.
            onClick={blocked || option.isActive ? undefined : option.onSelect}
            data-testid={optionTestId ? optionTestId(option) : `${testId}-${option.value}`}
            data-pill-state={option.value}
            data-pill-active={option.isActive ? 'true' : 'false'}
            data-pill-inert={option.inert ? 'true' : undefined}
            {...(option.dataAttrs || {})}
          >
            {option.label}
            {option.badge}
          </button>
        );
      })}
      {hasInert && inertHint && (
        <span id={inertHintId} className="sr-only">
          {inertHint}
        </span>
      )}
    </Wrapper>
  );
}

TogglePillGroup.propTypes = {
  options: PropTypes.arrayOf(TOGGLE_PILL_OPTION_SHAPE).isRequired,
  ariaLabel: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['sm', 'md']),
  testId: PropTypes.string.isRequired,
  optionTestId: PropTypes.func,
  inertHint: PropTypes.string,
  inertHintId: PropTypes.string,
  /** `'radio'` (default) = a value picker. `'tab'` = an ARIA tablist. */
  variant: PropTypes.oneOf(['radio', 'tab']),
  className: PropTypes.string,
};

export default TogglePillGroup;
